import React, { useMemo, useState, useEffect, useLayoutEffect, useRef } from 'react';
import stocksList from '../data/stocksList.json';
import { getStockQuote, StockQuote, getAfterHoursQuote, AfterHoursQuote, fetchHistoricalPrices, HistoricalPrice, fetchIntradayPrices, IntradayPrice, fetchBatchStockQuotes, searchStocks } from '../services/stockApi';
import { config } from '../config/config';
import { adminApi, type QuoteCacheDto } from '../services/adminApi';
import { notificationsApi, type NotificationDto } from '../services/notificationsApi';
import BugReportModal from './BugReportModal';
import {
  getMarketDayKey,
  getMarketProgress,
  getMarketSpecForExchange,
  getZonedParts,
  getNextMarketOpenUtc,
  getMarketSessionLabel,
  type MarketSpec,
  zonedTimeToUtc,
} from '../utils/marketTime';

interface StockListItem {
  symbol: string;
  name: string;
  exchange: string;
  category: string;
}

interface HeaderProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
  currentPage: string;
  onPageChange: (page: string) => void;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  selectedStock?: string;
  onSymbolChange?: (symbol: string) => void;
  balanceSheetPeriod?: 'annual' | 'quarter';
  onBalanceSheetPeriodChange?: (period: 'annual' | 'quarter') => void;

  // Optional main-app auth + saved stocks (Stock History)
  authUser?: { username: string } | null;
  authToken?: string | null;
  onQuickLogin?: (username?: string, password?: string) => void;
  onLogout?: () => void;
  authBusy?: boolean;
  savedStocks?: string[];
  savedStocksError?: string | null;
  onSelectSavedStock?: (symbol: string) => void;
  onSaveCurrentStock?: () => void;

  // Chip -> StockHistory sync
  onChipAdded?: (symbol: string) => void;
  onChipRemoved?: (symbol: string) => void | Promise<void>;

  // Action links
  onAddToMyWatchlist?: () => void;
  onAddToMyPortfolio?: () => void;
  onNotificationSymbolSelect?: (symbol: string) => void;
}

const Header: React.FC<HeaderProps> = ({
  isDarkMode,
  onToggleTheme,
  currentPage,
  onPageChange,
  activeTab = 'Charts',
  onTabChange,
  selectedStock = config.api?.defaultSymbol || 'AAPL',
  onSymbolChange,
  balanceSheetPeriod = 'annual',
  onBalanceSheetPeriodChange,
  authUser = null,
  authToken = null,
  onQuickLogin,
  onLogout,
  authBusy = false,
  savedStocks = [],
  savedStocksError = null,
  onSelectSavedStock,
  onSaveCurrentStock,
  onChipAdded,
  onChipRemoved,
  onAddToMyWatchlist,
  onAddToMyPortfolio,
  onNotificationSymbolSelect,
}) => {
  const headerRef = useRef<HTMLElement | null>(null);
  const researchBarsRef = useRef<HTMLDivElement | null>(null);
  const authMenuDetailsRef = useRef<HTMLDetailsElement | null>(null);
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const [isBugReportModalOpen, setIsBugReportModalOpen] = useState(false);
  const [bugReportBusy, setBugReportBusy] = useState(false);
  const [bugReportError, setBugReportError] = useState<string | null>(null);
  const [bugReportSuccess, setBugReportSuccess] = useState<string | null>(null);

  const closeAuthMenu = () => {
    if (authMenuDetailsRef.current) authMenuDetailsRef.current.open = false;
    setAuthMenuOpen(false);
  };

  const submitBugReport = async (payload: { title: string; category: string; html: string }) => {
    if (!authToken) {
      setBugReportError('You must be logged in to send a report.');
      return;
    }

    setBugReportBusy(true);
    setBugReportError(null);
    setBugReportSuccess(null);
    try {
      await adminApi.createBugReport(authToken, {
        title: payload.title,
        category: payload.category,
        html: payload.html,
        pageUrl: typeof window !== 'undefined' ? window.location.href : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });
      setBugReportSuccess('Report saved successfully.');
      setIsBugReportModalOpen(false);
    } catch (e: any) {
      setBugReportError(e?.message || 'Failed to save report');
    } finally {
      setBugReportBusy(false);
    }
  };

  useEffect(() => {
    if (!authMenuOpen) return;

    const onMouseDown = (ev: MouseEvent) => {
      const el = authMenuDetailsRef.current;
      if (!el) return;
      const target = ev.target as Node | null;
      if (target && el.contains(target)) return;
      closeAuthMenu();
    };

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') closeAuthMenu();
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [authMenuOpen]);

  const tryDecodeAuthFromJwt = (token?: string | null): { email: string | null; username: string | null } | null => {
    try {
      if (!token) return null;
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const payload = parts[1];
      // base64url -> base64
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const json = atob(padded);
      const data = JSON.parse(json);
      const emailRaw = data?.email || data?.Email || data?.upn || data?.preferred_username;
      const usernameRaw =
        data?.username ||
        data?.Username ||
        data?.unique_name ||
        data?.name ||
        data?.sub;

      const email = emailRaw ? String(emailRaw).trim() : '';
      const username = usernameRaw ? String(usernameRaw).trim() : '';

      return {
        email: email ? email : null,
        username: username ? username : null,
      };
    } catch {
      return null;
    }
  };

  const isVInvestAIEnabled = process.env.REACT_APP_ENABLE_VINVESTAI === 'true';
  const pages = ['Portfolios', 'Watchlist', 'Alerts', 'Research', ...(isVInvestAIEnabled ? ['VInvestAI'] : []), 'Recordings'];

  // Expose sticky offsets for Research sub-screens (Key Metrics time-range needs to stick below Header + Research bars).
  useLayoutEffect(() => {
    const root = document.documentElement;

    const update = () => {
      const headerH = headerRef.current?.getBoundingClientRect().height ?? 0;
      const barsH = (currentPage === 'Research')
        ? (researchBarsRef.current?.getBoundingClientRect().height ?? 0)
        : 0;

      root.style.setProperty('--main-header-height', `${Math.round(headerH)}px`);
      root.style.setProperty('--research-header-stack-height', `${Math.round(headerH + barsH)}px`);
    };

    update();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => update());
      if (headerRef.current) ro.observe(headerRef.current);
      if (researchBarsRef.current) ro.observe(researchBarsRef.current);
    }

    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [currentPage]);

  const uniqueStocksList = useMemo(() => {
    const list = stocksList as StockListItem[];
    // Only exclude pseudo tickers (keep real tickers searchable).
    const excluded = new Set(['MAG7']);
    const seen = new Set<string>();
    return list.filter((stock) => {
      if (excluded.has(stock.symbol)) return false;
      const key = String(stock.symbol || '').trim().toUpperCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, []);

  const selectedStockMeta = useMemo(() => {
    return uniqueStocksList.find((s) => s.symbol === selectedStock);
  }, [selectedStock, uniqueStocksList]);

  useEffect(() => {
    if (!isVInvestAIEnabled && currentPage === 'VInvestAI') {
      onPageChange('Research');
    }
  }, [currentPage, isVInvestAIEnabled, onPageChange]);

  // State for API data
  const [stockData, setStockData] = useState<StockQuote | null>(null);
  const [afterHoursData, setAfterHoursData] = useState<AfterHoursQuote | null>(null);
  const [quoteCache, setQuoteCache] = useState<QuoteCacheDto | null>(null);
  const [historicalPrices, setHistoricalPrices] = useState<HistoricalPrice[]>([]);
  const [intradayPrices, setIntradayPrices] = useState<IntradayPrice[]>([]);
  const [isResearchQuotesRefreshing, setIsResearchQuotesRefreshing] = useState(false);
  const researchRefreshTintTimeoutRef = useRef<number | null>(null);

  // Notifications (bell dropdown)
  const notificationsWrapRef = useRef<HTMLDivElement | null>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsBusy, setNotificationsBusy] = useState(false);
  const [notificationsError, setNotificationsError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);

  const unreadNotificationsCount = useMemo(() => {
    return (notifications || []).reduce((acc, n) => acc + (n && !n.isRead ? 1 : 0), 0);
  }, [notifications]);

  const parseUtcIso = (value: string): Date => {
    const raw = String(value || '').trim();
    if (!raw) return new Date(NaN);
    const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw);
    return new Date(hasZone ? raw : `${raw}Z`);
  };

  const formatRelativeTime = (utcIso: string): string => {
    const dt = parseUtcIso(utcIso);
    const ms = dt.getTime();
    if (!Number.isFinite(ms)) return '';
    const diffMs = Date.now() - ms;
    const sec = Math.max(0, Math.floor(diffMs / 1000));
    if (sec < 45) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min${min === 1 ? '' : 's'} ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
    const day = Math.floor(hr / 24);
    return `${day} day${day === 1 ? '' : 's'} ago`;
  };

  const getNotificationIcon = (title: string): string => {
    return '/images/redalert.svg';
  };

  const isNotificationRecent = (utcIso: string | null | undefined): boolean => {
    if (!utcIso) return false;
    const ts = parseUtcIso(utcIso).getTime();
    if (!Number.isFinite(ts)) return false;
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - ts <= sevenDaysMs;
  };

  const loadNotifications = async (): Promise<void> => {
    if (!authToken) {
      setNotifications([]);
      setNotificationsError(null);
      return;
    }

    setNotificationsBusy(true);
    try {
      const rows = await notificationsApi.list(authToken, 500);
      const next = Array.isArray(rows) ? rows.filter((row) => isNotificationRecent(row?.createdAtUtc)) : [];
      setNotifications(next);
      setNotificationsError(null);
    } catch (e: any) {
      setNotificationsError(String(e?.message || e || 'Failed to load notifications'));
    } finally {
      setNotificationsBusy(false);
    }
  };

  useEffect(() => {
    if (!authToken) {
      setNotifications([]);
      setNotificationsOpen(false);
      setNotificationsError(null);
      return;
    }

    loadNotifications();
    const id = window.setInterval(() => {
      loadNotifications();
    }, 15000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    if (!notificationsOpen) return;
    loadNotifications();

    const onMouseDown = (ev: MouseEvent) => {
      const el = notificationsWrapRef.current;
      if (!el) return;
      const target = ev.target as Node | null;
      if (target && el.contains(target)) return;
      setNotificationsOpen(false);
    };

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsOpen]);

  const displayStockName = selectedStockMeta?.name || stockData?.name || selectedStock;
  const displayExchange = selectedStockMeta?.exchange || stockData?.exchange || 'NASDAQ';

  const authFromJwt = useMemo(() => {
    return tryDecodeAuthFromJwt(authToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);
  const authEmail = authFromJwt?.email ?? null;
  const isConnected = Boolean(authToken);
  const authDisplayName =
    authUser?.username ||
    authFromJwt?.username ||
    (authEmail ? authEmail.split('@')[0] : null) ||
    (isConnected ? 'Connected' : 'Not connected');
  const avatarLetterSource = authUser?.username || authFromJwt?.username || authEmail || 'A';
  const avatarLetter = String(avatarLetterSource).slice(0, 1).toUpperCase();

  const marketSpec: MarketSpec = useMemo(() => {
    // Prefer the curated exchange from our stock list (stable), otherwise fall back to API-provided exchange.
    const exchangeForMarket = selectedStockMeta?.exchange || displayExchange;
    return getMarketSpecForExchange(exchangeForMarket);
  }, [displayExchange, selectedStockMeta?.exchange]);
  const [searchInput, setSearchInput] = useState<string>('');
  // Logged-out default chip set.
  // Keep it aligned with the "preferredSymbols" list used in App.tsx (unique, up to 8).
  // On refresh (no login), chips reset to this list.
  const defaultChipSymbols = useMemo(() => {
    const preferred = [
      config.api?.defaultSymbol || 'AAPL',
      'AAPL',
      'GOOG',
      'MSFT',
      'AMZN',
      'NVDA',
      'META',
      'TSLA',
    ];
    const out: string[] = [];
    const seen = new Set<string>();
    for (const sym of preferred) {
      const s = String(sym || '').trim().toUpperCase();
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
      if (out.length >= 8) break;
    }
    return out;
  }, []);
  const [stockSymbols, setStockSymbols] = useState<string[]>(() => {
    // On refresh, avoid showing default chips for a second and then reordering.
    // If user is authenticated, wait for DB-backed stock history.
    if (authUser?.username) return [];
    return defaultChipSymbols;
  });
  const [chipQuotesMap, setChipQuotesMap] = useState<Record<string, StockQuote>>({});
  const [pendingChipSymbols, setPendingChipSymbols] = useState<string[]>([]);
  const [hiddenChipSymbols, setHiddenChipSymbols] = useState<string[]>([]);
  const lastAuthUsernameRef = useRef<string | null>(null);
  const [showAutocomplete, setShowAutocomplete] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [remoteSearchResults, setRemoteSearchResults] = useState<StockListItem[]>([]);
  const [remoteSearchBusy, setRemoteSearchBusy] = useState<boolean>(false);
  const [searchListSource, setSearchListSource] = useState<'api' | 'file'>('file');
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const stockSymbolsRef = useRef<string[]>([]);
  useEffect(() => {
    stockSymbolsRef.current = stockSymbols;
  }, [stockSymbols]);

  // Fetch day-change quotes for all chips whenever the list changes.
  useEffect(() => {
    if (!config.useRealAPI || stockSymbols.length === 0) return;
    let cancelled = false;
    fetchBatchStockQuotes(stockSymbols).then((map) => {
      if (!cancelled) setChipQuotesMap(map);
    });
    return () => { cancelled = true; };
  }, [stockSymbols]);

  useEffect(() => {
    const raw = searchInput.trim();
    if (!raw) {
      setRemoteSearchResults([]);
      setRemoteSearchBusy(false);
      return;
    }

    if (!config.useRealAPI) {
      setSearchListSource('file');
      setRemoteSearchResults([]);
      setRemoteSearchBusy(false);
      return;
    }

    let cancelled = false;
    setRemoteSearchBusy(true);
    const timeoutId = window.setTimeout(async () => {
      try {
        const results = await searchStocks(raw, 10);
        if (cancelled) return;
        const mapped = results.map((result) => {
          const sector = String(result.sector || '').trim();
          const industry = String(result.industry || '').trim();
          const category = sector && industry
            ? `${sector} / ${industry}`
            : (sector || industry || '');
          const exchange = result.exchangeShortName
            || result.stockExchange
            || (result as { exchange?: string }).exchange
            || '';
          return {
            symbol: result.symbol,
            name: result.name,
            exchange,
            category,
          };
        });
        const deduped = Array.from(
          new Map(
            mapped
              .filter((row) => String(row.symbol || '').trim().length > 0)
              .map((row) => [String(row.symbol || '').trim().toUpperCase(), row])
          ).values()
        );
        setRemoteSearchResults(deduped);
        setSearchListSource('api');
      } catch {
        if (!cancelled) {
          setRemoteSearchResults([]);
          setSearchListSource('file');
        }
      } finally {
        if (!cancelled) setRemoteSearchBusy(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [searchInput]);

  useEffect(() => {
    const nextUser = authUser?.username ? String(authUser.username).trim().toLowerCase() : null;
    if (lastAuthUsernameRef.current === nextUser) return;
    lastAuthUsernameRef.current = nextUser;

    // Prevent cross-user chip bleed by clearing user-specific local chip state.
    setPendingChipSymbols([]);
    setHiddenChipSymbols([]);
    setStockSymbols(nextUser ? [] : defaultChipSymbols);
  }, [authUser?.username, defaultChipSymbols]);

  const mergeChipSymbols = (saved: string[], pending: string[], hidden: string[]) => {
    const out: string[] = [];
    const seen = new Set<string>();
    const hiddenSet = new Set(hidden.map((s) => String(s || '').trim().toUpperCase()).filter(Boolean));
    const push = (sym: string) => {
      const s = String(sym || '').trim().toUpperCase();
      if (!s || seen.has(s) || hiddenSet.has(s)) return;
      seen.add(s);
      out.push(s);
    };
    for (const sym of pending) push(sym);
    for (const sym of saved) push(sym);
    return out.slice(0, 8);
  };

  const arraysEqual = (a: string[], b: string[]) => {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  const quickLoginSeedUsers = useMemo(() => ['admin', 'alonh', 'shaimuli'], []);
  const quickLoginPasswordByUser = useMemo(() => new Map<string, string>([
    ['admin', 'admin'],
    ['alonh', 'alonh'],
    ['shaimuli', '123456'],
  ]), []);
  const [quickLoginUsers, setQuickLoginUsers] = useState<string[]>(quickLoginSeedUsers);
  const [quickLoginUser, setQuickLoginUser] = useState<string>(quickLoginSeedUsers[0]);

  useEffect(() => {
    let disposed = false;

    adminApi
      .listPublicUsernames(authToken)
      .then((list) => {
        if (disposed) return;
        const cleaned = (list || [])
          .map((u) => String(u || '').trim())
          .filter((u) => u.length > 0);
        const unique = Array.from(new Set([...quickLoginSeedUsers, ...cleaned]));
        if (unique.length === 0) return;

        setQuickLoginUsers(unique);
        setQuickLoginUser((prev) => (unique.includes(prev) ? prev : unique[0]));
      })
      .catch(() => {
        // Keep defaults on failure.
      });

    return () => {
      disposed = true;
    };
  }, [authToken, quickLoginSeedUsers]);

  const showStockChips = currentPage === 'Research';
  const isChipActive = (symbol: string) => showStockChips && symbol === selectedStock;

  // When logged out, ensure the currently selected stock is present in the chip list
  // so it can be highlighted when entering/returning to Research.
  useEffect(() => {
    if (!showStockChips) return;
    if (authUser) return; // Logged-in chips are sourced only from DB-backed savedStocks.

    const normalized = (selectedStock || '').trim().toUpperCase();
    if (!normalized) return;

    setStockSymbols((prev) => {
      const list = (prev || []).map((s) => String(s || '').trim().toUpperCase()).filter(Boolean);
      if (list.includes(normalized)) return prev;
      return [normalized, ...list].slice(0, 8);
    });
  }, [authUser, selectedStock, showStockChips]);

  // Fetch stock data when selectedStock changes
  useEffect(() => {
    let disposed = false;
    let pollTimeoutId: number | null = null;

    const shouldUseQuotesCache = currentPage === 'Research' && Boolean(authToken);

    type ClientMarketPhase = 'PreOpen' | 'Regular' | 'OffHours';
    const PREOPEN_WINDOW_MIN = 10;

    const getClientMarketPhase = (spec: MarketSpec, now: Date): ClientMarketPhase => {
      const parts = getZonedParts(now, spec.timeZone);
      const isMarketDay = spec.openWeekdays.includes(parts.weekday);
      if (!isMarketDay) return 'OffHours';

      const currentMinutes = parts.hour * 60 + parts.minute;
      const preOpenStartMinutes = Math.max(0, spec.openMinutes - PREOPEN_WINDOW_MIN);

      if (currentMinutes >= spec.openMinutes && currentMinutes < spec.closeMinutes) return 'Regular';
      if (currentMinutes >= preOpenStartMinutes && currentMinutes < spec.openMinutes) return 'PreOpen';
      return 'OffHours';
    };

    const getClientPollMs = (phase: ClientMarketPhase): number => {
      switch (phase) {
        case 'PreOpen':
          return 2 * 60 * 1000;
        case 'Regular':
          return 4 * 60 * 1000;
        default:
          return 2 * 60 * 60 * 1000;
      }
    };

    const getNextBoundaryUtc = (spec: MarketSpec, now: Date): Date => {
      const parts = getZonedParts(now, spec.timeZone);

      const openHour = Math.floor(spec.openMinutes / 60);
      const openMinute = spec.openMinutes % 60;

      const closeHour = Math.floor(spec.closeMinutes / 60);
      const closeMinute = spec.closeMinutes % 60;

      const preOpenStartMinutes = Math.max(0, spec.openMinutes - PREOPEN_WINDOW_MIN);
      const preOpenHour = Math.floor(preOpenStartMinutes / 60);
      const preOpenMinute = preOpenStartMinutes % 60;

      const isMarketDay = spec.openWeekdays.includes(parts.weekday);
      const todayPreOpenUtc = isMarketDay
        ? zonedTimeToUtc({ year: parts.year, month: parts.month, day: parts.day, hour: preOpenHour, minute: preOpenMinute }, spec.timeZone)
        : null;
      const todayOpenUtc = isMarketDay
        ? zonedTimeToUtc({ year: parts.year, month: parts.month, day: parts.day, hour: openHour, minute: openMinute }, spec.timeZone)
        : null;
      const todayCloseUtc = isMarketDay
        ? zonedTimeToUtc({ year: parts.year, month: parts.month, day: parts.day, hour: closeHour, minute: closeMinute }, spec.timeZone)
        : null;

      if (todayPreOpenUtc && now.getTime() < todayPreOpenUtc.getTime()) return todayPreOpenUtc;
      if (todayOpenUtc && now.getTime() < todayOpenUtc.getTime()) return todayOpenUtc;
      if (todayCloseUtc && now.getTime() < todayCloseUtc.getTime()) return todayCloseUtc;

      // After close / non-market-day: next boundary is next market day's pre-open.
      const nextOpen = getNextMarketOpenUtc(spec, now);
      return new Date(nextOpen.getTime() - PREOPEN_WINDOW_MIN * 60 * 1000);
    };

    const fetchData = async (reason: 'initial' | 'poll') => {
      if (disposed) return;

      const isResearch = currentPage === 'Research';
      if (isResearch) {
        if (researchRefreshTintTimeoutRef.current != null) {
          window.clearTimeout(researchRefreshTintTimeoutRef.current);
          researchRefreshTintTimeoutRef.current = null;
        }
        setIsResearchQuotesRefreshing(true);
      }

      try {
        if (shouldUseQuotesCache) {
          const requestedSymbols = (() => {
            const out: string[] = [];
            const seen = new Set<string>();
            const push = (sym: string) => {
              const s = String(sym || '').trim().toUpperCase();
              if (!s || seen.has(s)) return;
              seen.add(s);
              out.push(s);
            };

            for (const sym of (stockSymbolsRef.current || [])) push(sym);
            push(selectedStock);
            return out.slice(0, 8);
          })();

          const [{ rows, meta }, afterHours, historical, intraday] = await Promise.all([
            adminApi.getQuotesCacheWithMeta(String(authToken), requestedSymbols).catch(() => ({
              rows: [] as QuoteCacheDto[],
              meta: { phase: null, ttlSec: null, staleCount: null },
            })),
            getAfterHoursQuote(selectedStock),
            fetchHistoricalPrices(selectedStock),
            fetchIntradayPrices(selectedStock),
          ]);

          if (disposed) return;

          const responseSymbols = new Set(
            (rows || []).map((r) => String(r?.symbol || '').trim().toUpperCase()).filter(Boolean)
          );
          const missingSymbols = requestedSymbols.filter((s) => !responseSymbols.has(String(s || '').trim().toUpperCase()));

          // Expose last cache payload for manual verification in DevTools.
          (window as any).__garpQuotesCacheLast = {
            at: new Date().toISOString(),
            reason,
            backendBaseUrl: adminApi.baseUrl(),
            selectedStock,
            requestedSymbols,
            meta,
            missingSymbols,
            rows,
          };

          const normalizedSelected = String(selectedStock || '').trim().toUpperCase();
          const maybeCached = (rows || []).find((r) => String(r?.symbol || '').trim().toUpperCase() === normalizedSelected) ?? null;
          const hasValidCachedPrice = Boolean(maybeCached && maybeCached.currentPrice != null && Number(maybeCached.currentPrice) > 0);

          setQuoteCache(hasValidCachedPrice ? maybeCached : null);

          if (hasValidCachedPrice) {
            setStockData(null);
          } else {
            // Cache miss: fall back to real quote API (legacy behavior).
            const data = await getStockQuote(selectedStock);
            if (disposed) return;
            setStockData(data);

            if (data && data.price > 0) {
              void adminApi.upsertQuotesCache(String(authToken), {
                symbol: selectedStock,
                currentPrice: data.price,
                changePct: (typeof data.changesPercentage === 'number' ? data.changesPercentage : null),
              }).catch(() => {
                // best-effort
              });
            }
          }

          setAfterHoursData(afterHours);
          setHistoricalPrices(historical);
          setIntradayPrices(intraday);
          return;
        }

        const [data, afterHours, historical, intraday] = await Promise.all([
          getStockQuote(selectedStock),
          getAfterHoursQuote(selectedStock),
          fetchHistoricalPrices(selectedStock),
          fetchIntradayPrices(selectedStock),
        ]);

        if (disposed) return;
        setQuoteCache(null);
        setStockData(data);
        setAfterHoursData(afterHours);
        setHistoricalPrices(historical);
        setIntradayPrices(intraday);
      } finally {
        if (!disposed && currentPage === 'Research') {
          researchRefreshTintTimeoutRef.current = window.setTimeout(() => {
            setIsResearchQuotesRefreshing(false);
            researchRefreshTintTimeoutRef.current = null;
          }, 2000);
        }
      }
    };

    void fetchData('initial');

    if (shouldUseQuotesCache) {
      const scheduleNext = () => {
        if (disposed) return;
        const now = new Date();
        const phase = getClientMarketPhase(marketSpec, now);
        const pollMs = getClientPollMs(phase);
        const boundary = getNextBoundaryUtc(marketSpec, now);
        const untilBoundaryMs = Math.max(0, boundary.getTime() - now.getTime()) + 750;
        const delayMs = Math.max(1_000, Math.min(pollMs, untilBoundaryMs));

        (window as any).__garpQuotesCacheLast = {
          ...(window as any).__garpQuotesCacheLast,
          clientPhase: phase,
          clientPollMs: pollMs,
          nextBoundaryUtc: boundary.toISOString(),
          nextPollInMs: delayMs,
        };

        pollTimeoutId = window.setTimeout(async () => {
          await fetchData('poll');
          scheduleNext();
        }, delayMs);
      };

      scheduleNext();
    }

    return () => {
      disposed = true;
      if (researchRefreshTintTimeoutRef.current != null) {
        window.clearTimeout(researchRefreshTintTimeoutRef.current);
        researchRefreshTintTimeoutRef.current = null;
      }
      if (pollTimeoutId != null) window.clearTimeout(pollTimeoutId);
    };
  }, [selectedStock, currentPage, authToken, marketSpec]);

  // Keep intraday series fresh while on Research (any tab).
  useEffect(() => {
    if (currentPage !== 'Research') return;
    const intervalId = window.setInterval(async () => {
      const intraday = await fetchIntradayPrices(selectedStock);
      setIntradayPrices(intraday);
    }, 60_000);
    return () => window.clearInterval(intervalId);
  }, [currentPage, selectedStock]);

  // Reset intraday exactly at the next regular-session open (market timezone), then refetch.
  // This makes intraday start "from the beginning" even if the user is in a different timezone.
  useEffect(() => {
    let openTimeout: number | undefined;

    const schedule = () => {
      const next = getNextMarketOpenUtc(marketSpec, new Date());

      if (openTimeout != null) window.clearTimeout(openTimeout);
      const ms = Math.max(0, next.getTime() - Date.now());
      openTimeout = window.setTimeout(async () => {
        setIntradayPrices([]);
        try {
          const intraday = await fetchIntradayPrices(selectedStock);
          setIntradayPrices(intraday);
        } catch {
          // Best effort; the 60s refresh loop will retry.
        }
        schedule();
      }, ms + 250);
    };

    schedule();
    return () => {
      if (openTimeout != null) window.clearTimeout(openTimeout);
    };
  }, [marketSpec, selectedStock]);

  // Fallback: if a timeout gets canceled/rescheduled (e.g. exchange string update),
  // detect the transition into regular session and force a reset/refetch once per market day.
  const lastOpenResetDayKeyRef = useRef<string>('');
  useEffect(() => {
    let intervalId: number | undefined;

    const tick = async () => {
      const mp = getMarketProgress(marketSpec, new Date());
      if (!mp.isMarketOpenNow) return;

      const todayKey = getMarketDayKey(new Date(), marketSpec);
      if (lastOpenResetDayKeyRef.current === todayKey) return;

      // Mark first to avoid loops if fetch fails.
      lastOpenResetDayKeyRef.current = todayKey;
      try {
        setIntradayPrices([]);
        const intraday = await fetchIntradayPrices(selectedStock);
        setIntradayPrices(intraday);
      } catch {
        // Best effort; the existing refresh loop will retry.
      }
    };

    intervalId = window.setInterval(() => void tick(), 10_000);
    void tick();

    return () => {
      if (intervalId != null) window.clearInterval(intervalId);
    };
  }, [marketSpec, selectedStock]);

  // Filter stocks based on search input, ranked so exact/prefix symbol matches win.
  // This prevents common substrings (e.g., "ame" in "American") from hiding tickers like AME.
  const filteredStocks = useMemo(() => {
    const raw = searchInput.trim();
    if (!raw) return [] as StockListItem[];

    const query = raw.toLowerCase();
    const querySym = raw.toUpperCase();

    const score = (stock: StockListItem): number => {
      const sym = (stock.symbol || '').toUpperCase();
      const name = (stock.name || '').toLowerCase();
      const cat = (stock.category || '').toLowerCase();

      if (sym === querySym) return 1000;
      if (sym.startsWith(querySym)) return 900;
      if (sym.includes(querySym)) return 800;
      if (name.startsWith(query)) return 700;
      if (name.includes(query)) return 600;
      if (cat.includes(query)) return 500;
      return 0;
    };

    const localScored = uniqueStocksList
      .map((stock) => ({ stock, s: score(stock) }))
      .filter((row) => row.s > 0)
      .sort((a, b) => (b.s - a.s) || a.stock.symbol.localeCompare(b.stock.symbol))
      .slice(0, 10)
      .map((row) => row.stock);

    if (config.useRealAPI && searchListSource === 'api') {
      return remoteSearchResults
        .map((stock) => ({ stock, s: score(stock) }))
        .filter((row) => row.s > 0)
        .sort((a, b) => (b.s - a.s) || a.stock.symbol.localeCompare(b.stock.symbol))
        .slice(0, 10)
        .map((row) => row.stock);
    }

    return localScored;
  }, [searchInput, remoteSearchResults, searchListSource, uniqueStocksList]);

  // Handle clicking outside to close autocomplete
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowAutocomplete(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setShowAutocomplete(e.target.value.trim().length > 0);
    setSelectedIndex(-1);
  };

  // Handle selecting a stock from autocomplete
  const handleSelectStock = (symbol: string) => {
    const normalized = (symbol || '').trim().toUpperCase();
    if (normalized.length === 0) return;

    if (authUser) {
      setPendingChipSymbols((prev) => {
        if (prev.includes(normalized)) return prev;
        return [normalized, ...prev].slice(0, 8);
      });
      setHiddenChipSymbols((prev) => prev.filter((s) => s !== normalized));
      setStockSymbols((prev) => {
        if (prev.includes(normalized)) return prev;
        const updated = [normalized, ...prev];
        return updated.slice(0, 8);
      });
    } else {
      // User requirement: newly added symbol should appear left-most.
      // Keep uniqueness and cap to 8.
      setStockSymbols(prev => {
        if (prev.includes(normalized)) return prev;
        const updated = [normalized, ...prev];
        return updated.slice(0, 8);
      });
    }
    
    // Set as current symbol and fetch data
    onChipAdded?.(normalized);
    onSymbolChange?.(normalized);
    setSearchInput('');
    setShowAutocomplete(false);
    setSelectedIndex(-1);
  };

  // Handle keyboard navigation in autocomplete
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showAutocomplete || filteredStocks.length === 0) {
      if (e.key === 'Enter') {
        handleSearch(e);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredStocks.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredStocks.length) {
          handleSelectStock(filteredStocks[selectedIndex].symbol);
        } else if (searchInput.trim()) {
          handleSearch(e);
        }
        break;
      case 'Escape':
        setShowAutocomplete(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle search submission
  const handleSearch = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchInput.trim()) {
      const symbol = searchInput.trim().toUpperCase();
      
      // Update stockSymbols: newly added symbol should be left-most.
      if (authUser) {
        setPendingChipSymbols((prev) => {
          if (prev.includes(symbol)) return prev;
          return [symbol, ...prev].slice(0, 8);
        });
        setHiddenChipSymbols((prev) => prev.filter((s) => s !== symbol));
        setStockSymbols(prev => {
          if (prev.includes(symbol)) return prev;
          const updated = [symbol, ...prev];
          return updated.slice(0, 8);
        });
      } else {
        setStockSymbols(prev => {
          if (prev.includes(symbol)) return prev;
          const updated = [symbol, ...prev];
          return updated.slice(0, 8);
        });
      }
      
      // Set as current symbol and fetch data
      onChipAdded?.(symbol);
      onSymbolChange?.(symbol);
      setSearchInput(''); // Clear search input after submission
    }
  };

  // Handle clicking on a stock chip
  const handleChipClick = (symbol: string) => {
    const normalized = (symbol || '').trim().toUpperCase();
    if (!normalized) return;

    // Log the selection in DB-backed history so it survives refresh/logout.
    onChipAdded?.(normalized);

    onSymbolChange?.(normalized);
  };

  // Handle removing a stock chip
  const handleRemoveChip = async (symbol: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent chip click event

    const normalized = (symbol || '').trim().toUpperCase();
    if (!normalized) return;

    const isPendingChip = pendingChipSymbols.includes(normalized);

    // If we have a backend-sync hook, wait for it.
    // This prevents a confusing state where the chip disappears in the UI
    // but isn't deleted in the backend.
    if (onChipRemoved) {
      try {
        await onChipRemoved(normalized);
        setHiddenChipSymbols((prev) => prev.filter((s) => s !== normalized));
      } catch {
        if (!isPendingChip) {
          setHiddenChipSymbols((prev) => {
            if (prev.includes(normalized)) return prev;
            return [...prev, normalized];
          });
        }
      }
    }

    setStockSymbols(prev => prev.filter(s => s !== normalized));
    setPendingChipSymbols(prev => prev.filter(s => s !== normalized));

    // If removed symbol was current, switch to first remaining symbol
    if (normalized === selectedStock) {
      const remaining = stockSymbols.filter(s => (s || '').trim().toUpperCase() !== normalized);
      const next = (remaining[0] || '').trim().toUpperCase();
      if (next) {
        onChipAdded?.(next);
        onSymbolChange?.(next);
      }
    }
  };

  // When logged-in, reflect DB-backed saved stocks in the chip list.
  useEffect(() => {
    if (authUser) {
      if (!savedStocks || savedStocks.length === 0) {
        // Wait for DB-backed stock history to load; do not show defaults.
        const merged = mergeChipSymbols([], pendingChipSymbols, hiddenChipSymbols);
        setStockSymbols((prev) => (arraysEqual(prev, merged) ? prev : merged));
        return;
      }
      const savedNormalized = savedStocks.map((s) => String(s || '').trim().toUpperCase());
      const pendingFiltered = pendingChipSymbols.filter(
        (s) => !savedNormalized.includes(String(s || '').trim().toUpperCase())
      );
      setPendingChipSymbols((prev) => (arraysEqual(prev, pendingFiltered) ? prev : pendingFiltered));
      const merged = mergeChipSymbols(savedNormalized, pendingFiltered, hiddenChipSymbols);
      setStockSymbols((prev) => (arraysEqual(prev, merged) ? prev : merged));
      return;
    }

    // Logged out: reset to defaults.
    setStockSymbols(defaultChipSymbols);
  }, [authUser, savedStocks, defaultChipSymbols, pendingChipSymbols, hiddenChipSymbols]);

  // Persist the currently rendered chips for the logged-in user.
  // This prevents a visual "jump" on refresh while savedStocks loads.
  useEffect(() => {
    if (!authUser?.username) return;
    // No local cache; chips should reflect DB-backed stock history.
  }, [authUser?.username, stockSymbols]);

  // Detect if browser is at 125% zoom
  const isZoomed125 = window.devicePixelRatio >= 1.25;
  const stockInfoBarHeight = isZoomed125 ? '3.25rem' : '5.25rem';

  // Prefer backend quotes-cache when available (avoids calling FMP quote API for price).
  const cachedPrice = quoteCache?.currentPrice ?? null;
  const cachedChangePct = quoteCache?.changePct ?? null;

  // API-only: if data is missing, show empty/neutral values (no JSON/mock fallback).
  const lastPrice = cachedPrice != null ? cachedPrice : ((stockData && stockData.price != null) ? stockData.price : 0);

  const previousPrice = (() => {
    if (stockData && stockData.previousClose != null) return stockData.previousClose;
    if (cachedChangePct != null && lastPrice > 0) {
      const denom = 1 + (cachedChangePct / 100);
      return denom !== 0 ? (lastPrice / denom) : lastPrice;
    }
    if (stockData && stockData.change != null && lastPrice) return lastPrice - stockData.change;
    return lastPrice;
  })();

  const priceChange = (() => {
    if (stockData && stockData.change != null) return stockData.change;
    return lastPrice - previousPrice;
  })();

  const percentChange = (() => {
    if (stockData && stockData.changesPercentage != null) return stockData.changesPercentage;
    if (cachedChangePct != null) return cachedChangePct;
    return previousPrice > 0 ? ((priceChange / previousPrice) * 100) : 0;
  })();
  
  const formattedPrice = `$${(lastPrice || 0).toFixed(2)}`;
  const formattedChange = `${priceChange >= 0 ? '+' : ''}$${Math.abs(priceChange || 0).toFixed(2)}`;
  const formattedPercent = `${priceChange >= 0 ? '+' : ''}${(percentChange || 0).toFixed(2)}%`;
  
  // Use timestamp from cache/API if available; otherwise fall back to today's date.
  const lastDate = (quoteCache && quoteCache.updatedAtUtc)
    ? new Date(quoteCache.updatedAtUtc)
    : ((stockData && stockData.timestamp)
      ? new Date(stockData.timestamp * 1000) // API timestamp is in seconds, need milliseconds
      : new Date());
  const formattedDate = `${String(lastDate.getMonth() + 1).padStart(2, '0')}/${String(lastDate.getDate()).padStart(2, '0')}`;
  
  const isPositive = priceChange >= 0;

  const getMarketProgressNow = (): { isMarketDay: boolean; isMarketOpenNow: boolean; progress: number } => {
    return getMarketProgress(marketSpec, new Date());
  };

  // User requirement: no pre-market; we only model regular session open/closed.
  const getMarketSession = () => {
    return getMarketSessionLabel(marketSpec, new Date());
  };
  
  const marketSession = getMarketSession();

  // Extended hours data from API
  const afterHoursPrice = afterHoursData?.lastPrice || lastPrice;
  const afterHoursChange = afterHoursPrice - lastPrice; // Difference from current market price
  const afterHoursPercent = (afterHoursChange / lastPrice) * 100;
  const isAfterHoursPositive = afterHoursChange >= 0;
  const formattedAfterHoursChange = `${afterHoursChange >= 0 ? '+' : ''}$${Math.abs(afterHoursChange).toFixed(2)}`;
  const formattedAfterHoursPercent = `(${afterHoursChange >= 0 ? '+' : ''}${afterHoursPercent.toFixed(2)}%)`;
  const formattedAfterHoursPrice = `$${afterHoursPrice.toFixed(2)}`;

  // Earnings date - Not available (all API endpoints return 403 Forbidden)
  const earningsDate = 'N/A';

  const resolveIntradaySeries = (): { series: Array<{ date: string; close: number }>; dayKey: string; isMarketDay: boolean } => {
    if (!intradayPrices || intradayPrices.length === 0) return { series: [], dayKey: '', isMarketDay: false };

    // Prefer points from the current market trading day if present; otherwise use the latest day in the payload.
    const now = new Date();
    const marketKey = getMarketDayKey(now, marketSpec);

    const last = intradayPrices[intradayPrices.length - 1];
    const lastKey = typeof last?.date === 'string' ? last.date.slice(0, 10) : '';

    const hasMarketDay = intradayPrices.some((p) => typeof p?.date === 'string' && p.date.startsWith(marketKey));
    const dayKey = hasMarketDay ? marketKey : lastKey;

    // Keep only regular-session candles (no pre-market / no after-hours) so the intraday chart
    // truly starts at the regular open (e.g. 09:30 US).
    const inRegularSession = (dateStr: string) => {
      const hhmm = dateStr.length >= 16 ? dateStr.slice(11, 16) : '';
      const [hhRaw, mmRaw] = hhmm.split(':');
      const hh = Number(hhRaw);
      const mm = Number(mmRaw);
      if (!Number.isFinite(hh) || !Number.isFinite(mm)) return false;
      const minutes = hh * 60 + mm;
      return minutes >= marketSpec.openMinutes && minutes <= marketSpec.closeMinutes;
    };

    const series = intradayPrices
      .filter((p) => typeof p?.date === 'string' && dayKey && p.date.startsWith(dayKey) && inRegularSession(p.date))
      .slice(-120);

    return { series, dayKey, isMarketDay: dayKey === marketKey };
  };

  // Mini chart should show INTRA-DAY on Research when possible.
  // API-only: if intraday/daily are unavailable, show empty series.
  const isResearchPage = currentPage === 'Research';
  const intradayResolved = isResearchPage ? resolveIntradaySeries() : { series: [], dayKey: '', isMarketDay: false };
  const intradaySeries = intradayResolved.series;
  const dailyFallback = (historicalPrices && historicalPrices.length > 0) ? historicalPrices.slice(-30) : [];

  // On Research prefer intraday; if it's missing (e.g. before open), fall back to daily.
  const chartSeries = isResearchPage
    ? (intradaySeries.length > 0 ? intradaySeries : dailyFallback)
    : dailyFallback;

  // Color should reflect the *daily* move (matches the daily change text),
  // not the performance across the whole 30-day window.
  const lastPriceChart = chartSeries.length > 0 ? chartSeries[chartSeries.length - 1].close : 0;
  const chartMove = lastPriceChart - previousPrice;
  void chartMove;

  // Calculate SVG sparkline for mini intraday chart
  const chartWidth = 144;
  const chartHeight = 36;
  const padding = 2;
  const baselineStartX = padding;
  const baselineEndX = chartWidth - padding;

  // For intraday: center line should represent yesterday's close (previous close),
  // so that up/down is relative to the daily move.
  const baselinePrice = (() => {
    const isUsingIntraday = isResearchPage && intradaySeries.length > 0;
    if (isUsingIntraday) {
      return previousPrice > 0 ? previousPrice : intradaySeries[0].close;
    }
    if (chartSeries.length > 0) return chartSeries[0].close;
    return previousPrice;
  })();

  // Plot movement relative to baseline.
  const deltas = chartSeries.map((d) => d.close - baselinePrice);
  const maxAbsDelta = Math.max(0.01, ...deltas.map((v) => Math.abs(v)));
  const midY = chartHeight / 2;
  const halfPlotHeight = (chartHeight - 2 * padding) / 2;

  // During market hours, do not stretch the partial intraday data across the full width.
  // Instead, map the last point to "now" (NY time) within the regular session window.
  const getSessionProgress = (): number => {
    // Only apply partial-fill behavior when we are actually plotting intraday for the NY trading day.
    if (intradaySeries.length === 0) return 1;
    if (!intradayResolved.isMarketDay) return 1;
    const mp = getMarketProgressNow();
    return mp.isMarketOpenNow ? mp.progress : 1;
  };

  const sessionProgress = getSessionProgress();
  const plotWidth = chartWidth - 2 * padding;
  const xMax = padding + plotWidth * sessionProgress;

  type SparkPoint = { x: number; y: number; delta: number };
  const sparkPoints: SparkPoint[] = chartSeries.map((d, i) => {
    const denom = Math.max(1, chartSeries.length - 1);
    const x = padding + (i / denom) * Math.max(0, xMax - padding);
    const delta = d.close - baselinePrice;
    const normalized = delta / maxAbsDelta; // [-1..1]
    const y = Math.min(chartHeight - padding, Math.max(padding, midY - normalized * halfPlotHeight));
    return { x, y, delta };
  });

  const buildSignedSegments = (sign: 'pos' | 'neg'): SparkPoint[][] => {
    const wants = (d: number) => (sign === 'pos' ? d >= 0 : d <= 0);
    const segments: SparkPoint[][] = [];
    let current: SparkPoint[] = [];

    const pushCurrent = () => {
      if (current.length >= 2) segments.push(current);
      current = [];
    };

    for (let i = 0; i < sparkPoints.length; i++) {
      const p = sparkPoints[i];
      const prev = i > 0 ? sparkPoints[i - 1] : null;

      if (!prev) {
        if (wants(p.delta)) current.push(p);
        continue;
      }

      const prevWants = wants(prev.delta);
      const currWants = wants(p.delta);

      // If the sign changes between prev and current, add a zero-crossing point (at midY)
      // so the colored segments meet the axis cleanly.
      const crossesZero = (prev.delta === 0 && p.delta !== 0) || (prev.delta !== 0 && p.delta === 0) || (prev.delta > 0 && p.delta < 0) || (prev.delta < 0 && p.delta > 0);
      if (crossesZero) {
        const denom = (prev.delta - p.delta);
        const t = denom !== 0 ? (prev.delta / denom) : 0.5;
        const tc = Math.max(0, Math.min(1, t));
        const xCross = prev.x + (p.x - prev.x) * tc;
        const cross: SparkPoint = { x: xCross, y: midY, delta: 0 };

        if (prevWants) current.push(cross);
        if (prevWants && !currWants) pushCurrent();
        if (!prevWants && currWants) current.push(cross);
      }

      if (currWants) {
        if (!current.length && prevWants) current.push(prev);
        current.push(p);
      } else if (prevWants) {
        pushCurrent();
      }
    }

    pushCurrent();
    return segments;
  };

  const posSegments = buildSignedSegments('pos');
  const negSegments = buildSignedSegments('neg');

  const buildAreaPath = (segment: SparkPoint[]): string => {
    if (segment.length < 2) return '';
    const start = segment[0];
    const end = segment[segment.length - 1];
    const line = segment.map((p) => `L ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
    return `M ${start.x.toFixed(2)} ${midY.toFixed(2)} ${line} L ${end.x.toFixed(2)} ${midY.toFixed(2)} Z`;
  };

  const headerStyle: React.CSSProperties = {
    height: '4.375rem', // 70px converted to rem (70/16 = 4.375)
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    boxShadow: isDarkMode ? '0px 2px 20.9px 0px #00000063' : '0px 6px 18px 0px rgba(107, 107, 107, 0.16)',
    borderBottom: isDarkMode ? 'none' : '1px solid #DADCE0',
    position: 'sticky',
    top: 0,
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
    padding: '0.15rem 0.625rem 0.15rem 3.2rem',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    fontFamily: 'Manrope',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
  };

  // Container for both bars with proper margins
  const barsContainerStyle: React.CSSProperties = {
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    padding: '0.9375rem 0.625rem 0px 0.625rem', // 15px gap after header; no extra bottom padding (tabs-bar -> content gap is handled by the screen)
    position: 'sticky',
    top: 'var(--main-header-height, 0px)',
    zIndex: 1100,
  };

  const barsWrapperStyle: React.CSSProperties = {
    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    borderRadius: '0.5rem',
    overflow: 'hidden',
  };

  // Stock Info Bar Styles (100px = 6.25rem at 100%, 68px = 4.25rem at 125%)
  const stockInfoBarStyle: React.CSSProperties = {
    height: stockInfoBarHeight,
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    display: 'flex',
    alignItems: 'center',
    padding: '0 1.5rem',
    gap: '2rem',
    border: `2px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    boxSizing: 'border-box',
    borderRadius: '7px',
  };

  const stockLogoStyle: React.CSSProperties = {
    width: '3rem',
    height: '3rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
  };

  const stockInfoStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  };

  const stockNameStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
  };

  const stockSymbolStyle: React.CSSProperties = {
    fontSize: '1rem',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
  };

  const stockPriceStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: '400',
    color: isDarkMode ? '#F9FAFB' : '#1f2937',
  };

  const stockChangeStyle: React.CSSProperties = {
    fontSize: '1rem',
    color: isPositive ? '#12B76A' : '#FF0052',
    fontWeight: '500',
  };

  const stockDetailsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1.5rem',
    fontSize: '0.875rem',
    color: isDarkMode ? '#F9FAFB' : '#6b7280',
  };

  const earningsDateStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: isDarkMode ? '#F9FAFB' : '#6b7280',
    fontWeight: '500',
  };

  const actionLinksStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    marginLeft: 'auto',
  };

  const actionLinkStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#2F58F2',
    cursor: 'pointer',
    textDecoration: 'none',
  };

  // Tabs Bar Styles (34px = 2.125rem)
  const tabsBarStyle: React.CSSProperties = {
    height: '2.125rem',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    display: 'flex',
    alignItems: 'center',
    padding: '0rem 0.75rem',
    justifyContent: 'space-between',
    columnGap: 'clamp(0.2rem, 0.9vw, 0.85rem)',
    rowGap: '0.4rem',
    flexWrap: 'wrap',
    boxSizing: 'border-box',
    width: '100%',
    border: `1px solid ${isDarkMode ? '#374151' : 'transparent'}`,
  };

  const tabStyle = (isActive: boolean, isDisabled: boolean = false): React.CSSProperties => ({
    fontSize: '1rem',
    fontWeight: isActive ? '700' : '400',
    color: isDisabled ? '#999999' : (isActive ? (isDarkMode ? '#000000' : '#ffffff') : (isDarkMode ? '#F9FAFB' : '#000000')),
    backgroundColor: isActive ? (isDarkMode ? '#3B82F6' : '#2F58F2') : 'transparent',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    padding: '0.12rem 0.52rem',
    borderRadius: '0.375rem',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
    opacity: isDisabled ? 0.5 : 1,
  });

  const logoContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  };

  const logoTextStyle: React.CSSProperties = {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    whiteSpace: 'nowrap',
  };

  const betaBadgeStyle: React.CSSProperties = {
    backgroundColor: '#2F58F2',
    color: '#ffffff',
    fontSize: '0.75rem',
    fontWeight: '400',
    padding: '0.2rem 0.375rem',
    borderRadius: '1.25rem',
    textTransform: 'uppercase',
    lineHeight: 1,
  };

  const navContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem',
    flex: 1,
    marginLeft: '2rem',
  };

  const navItemStyle = (isActive: boolean): React.CSSProperties => ({
    fontSize: '1rem',
    fontWeight: isActive ? '800' : '400',
    color: isDarkMode ? '#F9FAFB' : (isActive ? '#0D0D0D' : '#000000'),
    cursor: 'pointer',
    padding: '0.5rem 0',
    whiteSpace: 'nowrap',
  });

  const rightSectionStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  };

  const searchContainerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const searchInputStyle: React.CSSProperties = {
    width: '220px',
    height: '40px',
    padding: '0 0.75rem 0 2.25rem',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
    borderRadius: '8px',
    fontSize: '0.875rem',
    color: isDarkMode ? '#F9FAFB' : '#4b5563',
    outline: 'none',
    opacity: 1,
  };

  const searchIconStyle: React.CSSProperties = {
    position: 'absolute',
    left: '0.625rem',
    width: '16px',
    height: '16px',
    color: isDarkMode ? '#9ca3af' : '#6b7280',
  };

  const stockChipsContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const stockChipStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    position: 'relative',
    width: '82px',
    height: '38px',
    padding: 0,
    backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
    border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
    borderRadius: '0.375rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    flexShrink: 0,
  };

  const closeIconStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: isDarkMode ? '#9ca3af' : '#6b7280',
    cursor: 'pointer',
  };

  const iconButtonStyle: React.CSSProperties = {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: isDarkMode ? '#e5e7eb' : '#374151',
    transition: 'background-color 0.2s ease',
  };

  const avatarStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const autocompleteContainerStyle: React.CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    width: '400px',
    maxHeight: '400px',
    overflowY: 'auto',
    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
    border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    zIndex: 1000,
  };

  const autocompleteItemStyle = (isSelected: boolean): React.CSSProperties => ({
    padding: '0.75rem 1rem',
    cursor: 'pointer',
    backgroundColor: isSelected 
      ? (isDarkMode ? '#374151' : '#f3f4f6')
      : 'transparent',
    borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    transition: 'background-color 0.15s ease',
  });

  const autocompleteSymbolStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: isDarkMode ? '#e5e7eb' : '#1f2937',
  };

  const autocompleteNameStyle: React.CSSProperties = {
    fontSize: '0.8125rem',
    color: isDarkMode ? '#9ca3af' : '#6b7280',
    marginTop: '0.125rem',
  };

  const autocompleteCategoryStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    color: '#2F58F2',
    marginTop: '0.125rem',
    fontWeight: '500',
  };

  const showChartFpmTab = false;
  const tabs = [
    'Summary',
    'Insights',
    'Key Metrics',
    'Balance Sheet',
    'Income Statement',
    'Cash Flow',
    'Growth',
    'Charts',
    ...(showChartFpmTab ? ['Chart fpm'] : []),
    'Back Testing',
    'Valuation',
    'Research Desk',
  ];

  const barsVerticalGapPx = 15;

  // No disabled research tabs.

  return (
    <>
      <header ref={headerRef} id="main-header" style={headerStyle}>
        {/* Logo and Beta Badge */}
        <div style={logoContainerStyle}>
          <span style={logoTextStyle}>
            {'GARP Investing Toolbo'}
            <span style={{ position: 'relative', display: 'inline-block' }}>
              {'x'}
              <span
                style={{
                  ...betaBadgeStyle,
                  position: 'absolute',
                  left: '50%',
                  top: '-0.87rem',
                  transform: 'translateX(-50%) translate(0.42rem, 0.22rem)',
                  pointerEvents: 'none',
                }}
              >
                PRO
              </span>
            </span>
          </span>
        </div>

      {/* Navigation */}
      <nav style={navContainerStyle}>
        {pages.map((page) => {
          const isActive = page === currentPage;

          return (
            <div
              key={page}
              role="button"
              tabIndex={0}
              style={navItemStyle(isActive)}
              onClick={() => onPageChange(page)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onPageChange(page);
                }
              }}
              onMouseEnter={(e) => {
                if (page !== currentPage && !isDarkMode) {
                  e.currentTarget.style.color = '#5A5A5A';
                }
              }}
              onMouseLeave={(e) => {
                if (page !== currentPage) {
                  e.currentTarget.style.color = isDarkMode ? '#F9FAFB' : '#000000';
                }
              }}
            >
              {page}
            </div>
          );
        })}
      </nav>

      {/* Right Section */}
      <div style={rightSectionStyle}>
        {/* Search */}
        <div id="search-container" style={searchContainerStyle}>
          <style>{`
            #search-input::placeholder {
              color: #D1D5DB;
            }
          `}</style>
          <svg style={searchIconStyle} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            id="search-input"
            ref={searchInputRef}
            type="text"
            placeholder="Search Symbol"
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => searchInput.trim() && setShowAutocomplete(true)}
            style={searchInputStyle}
          />
          {showAutocomplete && filteredStocks.length > 0 && (
            <div ref={autocompleteRef} style={autocompleteContainerStyle}>
              {filteredStocks.map((stock, index) => (
                  <div
                    key={`${stock.symbol}-${stock.exchange}-${index}`}
                  style={autocompleteItemStyle(index === selectedIndex)}
                  onClick={() => handleSelectStock(stock.symbol)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div style={autocompleteSymbolStyle}>
                    {stock.exchange ? `${stock.symbol} - ${stock.exchange}` : stock.symbol}
                  </div>
                  <div style={autocompleteNameStyle}>
                    {stock.name}
                  </div>
                  {stock.category ? (
                    <a
                      href="#"
                      style={autocompleteCategoryStyle}
                      onClick={(event) => event.preventDefault()}
                    >
                      {stock.category}
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stock Chips */}
        <div id="stock-chips-container" style={stockChipsContainerStyle}>
          {stockSymbols.map((symbol) => {
            const active = isChipActive(symbol);
            const chipQuote = chipQuotesMap[symbol];
            const chipChangePct = chipQuote ? chipQuote.changesPercentage : null;
            const chipPositive = chipChangePct != null ? chipChangePct >= 0 : null;
            const chipPctLabel = chipChangePct != null
              ? `${Math.abs(chipChangePct).toFixed(2)}%`
              : null;
            return (
              <div
                key={symbol}
                style={{
                  ...stockChipStyle,
                  backgroundColor: active ? 'rgb(47, 88, 242)' : '#F6F6F6',
                  borderColor: active ? 'rgb(47, 88, 242)' : '#D9D9D9',
                }}
                onClick={() => handleChipClick(symbol)}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.backgroundColor = '#F6F6F6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.backgroundColor = '#F6F6F6';
                  }
                }}
              >
                {/* Left content: 58px */}
                <div style={{
                  width: '58px',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  height: '100%',
                }}>
                  {/* Symbol */}
                  <span style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: 0,
                    textAlign: 'center',
                    width: '100%',
                    color: active ? '#FFFFFF' : '#0D0D0D',
                  }}>{symbol}</span>
                  {/* Bottom row: % change + arrow */}
                  {chipPctLabel && chipPositive !== null && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px', width: '100%' }}>
                      <span style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 500,
                        fontSize: '12px',
                        lineHeight: '100%',
                        letterSpacing: 0,
                        textAlign: 'center',
                        color: chipPositive ? '#12B76A' : '#FF0052',
                        transform: 'translateX(0px)',
                      }}>{chipPctLabel}</span>
                      <img
                        src={chipPositive ? '/images/greenicon.svg' : '/images/redicon.svg'}
                        alt={chipPositive ? 'up' : 'down'}
                        style={{
                          width: '10px',
                          height: '10px',
                          transform: chipPositive ? 'translateX(2px)' : 'translateX(2px) rotate(180deg)',
                        }}
                      />
                    </div>
                  )}
                </div>
                {/* Right X button: 24px */}
                <div
                  style={{
                    width: '24px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    cursor: 'pointer',
                  }}
                  onClick={(e) => handleRemoveChip(symbol, e)}
                >
                  <img
                    src="/images/remove.svg"
                    alt="remove"
                    style={{
                      width: '16px',
                      height: '16px',
                      opacity: 1,
                      filter: active ? 'brightness(10)' : (isDarkMode ? 'brightness(3)' : 'none'),
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Notification Bell */}
        <div ref={notificationsWrapRef} style={{ position: 'relative' }}>
          <button
            id="bell-button"
            type="button"
            aria-label="Notifications"
            style={{ ...iconButtonStyle, backgroundColor: 'transparent', position: 'relative' }}
            onClick={() => setNotificationsOpen((v) => !v)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/bell-01.svg`}
              alt="Notifications"
              style={{
                width: '18px',
                height: '18px',
                display: 'block',
                filter: isDarkMode ? 'invert(1)' : 'none',
                background: 'none',
                backgroundColor: 'transparent',
                borderRadius: 0,
                boxShadow: 'none',
                padding: 0,
                margin: 0,
              }}
            />

            {unreadNotificationsCount > 0 && (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: '5px',
                  right: '7px',
                  width: '10px',
                  height: '10px',
                  borderRadius: '999px',
                  backgroundColor: '#2F58F2',
                  border: `2px solid ${isDarkMode ? '#111827' : '#ffffff'}`,
                }}
              />
            )}
          </button>

          {notificationsOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                width: '340px',
                maxHeight: '420px',
                overflow: 'auto',
                backgroundColor: isDarkMode ? '#111827' : '#ffffff',
                border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                borderRadius: '12px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                zIndex: 2000,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 12px 10px 12px',
                  borderBottom: `1px solid ${isDarkMode ? '#374151' : '#f3f4f6'}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ fontWeight: 600, color: isDarkMode ? '#e5e7eb' : '#111827' }}>Notifications</div>
                  {unreadNotificationsCount > 0 && (
                    <div
                      style={{
                        fontSize: '12px',
                        lineHeight: 1,
                        padding: '4px 8px',
                        borderRadius: '999px',
                        backgroundColor: '#2F58F2',
                        color: '#ffffff',
                        fontWeight: 600,
                      }}
                    >
                      {unreadNotificationsCount}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  disabled={!authToken || unreadNotificationsCount === 0 || notificationsBusy}
                  onClick={async () => {
                    if (!authToken) return;
                    try {
                      setNotificationsBusy(true);
                      await notificationsApi.markAllRead(authToken);
                      setNotifications((prev) => (prev || []).map((n) => ({ ...n, isRead: true, readAtUtc: new Date().toISOString() })));
                    } catch {
                      // ignore; keep UI responsive
                    } finally {
                      setNotificationsBusy(false);
                    }
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: !authToken || unreadNotificationsCount === 0 || notificationsBusy ? 'default' : 'pointer',
                    color: (!authToken || unreadNotificationsCount === 0) ? (isDarkMode ? '#6b7280' : '#9ca3af') : '#2F58F2',
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: 0,
                  }}
                >
                  ✓✓ Mark all as Read
                </button>
              </div>

              {notificationsError && (
                <div style={{ padding: '10px 12px', color: isDarkMode ? '#fca5a5' : '#b91c1c', fontSize: '12px' }}>
                  {notificationsError}
                </div>
              )}

              {!notificationsError && notifications.length === 0 && !notificationsBusy && (
                <div style={{ padding: '14px 12px', color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: '13px' }}>
                  No notifications
                </div>
              )}

              <div>
                {(notifications || []).map((n) => {
                  const isUnread = !n.isRead;
                  return (
                    <div
                      key={String(n.id)}
                      role="button"
                      tabIndex={0}
                      onClick={async () => {
                        if (!authToken) return;
                        try {
                          if (isUnread) {
                            await notificationsApi.markRead(authToken, n.id);
                            setNotifications((prev) =>
                              (prev || []).map((x) => (x.id === n.id ? { ...x, isRead: true, readAtUtc: new Date().toISOString() } : x))
                            );
                          }
                        } catch {
                          // ignore
                        }

                        const symbol = String(n.symbol || '').trim().toUpperCase();
                        if (symbol) {
                          if (onNotificationSymbolSelect) {
                            onNotificationSymbolSelect(symbol);
                          } else if (onSymbolChange) {
                            onSymbolChange(symbol);
                          }
                          setNotificationsOpen(false);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          (e.currentTarget as any)?.click?.();
                        }
                      }}
                      style={{
                        display: 'flex',
                        gap: '10px',
                        padding: '12px',
                        cursor: authToken ? 'pointer' : 'default',
                        borderBottom: `1px solid ${isDarkMode ? '#374151' : '#f3f4f6'}`,
                        backgroundColor: 'transparent',
                      }}
                    >
                      <div style={{ width: '26px', display: 'flex', justifyContent: 'center', paddingTop: '2px' }}>
                        <div style={{ position: 'relative', width: '20px', height: '20px' }}>
                          <img
                            src={getNotificationIcon(n.title)}
                            alt=""
                            aria-hidden="true"
                            style={{ width: '20px', height: '20px', display: 'block', opacity: isDarkMode ? 0.9 : 0.85 }}
                          />
                          {isUnread && (
                            <span
                              aria-hidden="true"
                              style={{
                                position: 'absolute',
                                top: '-2px',
                                left: '-2px',
                                width: '10px',
                                height: '10px',
                                borderRadius: '999px',
                                backgroundColor: '#2F58F2',
                                border: `2px solid ${isDarkMode ? '#111827' : '#ffffff'}`,
                              }}
                            />
                          )}
                        </div>
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: isDarkMode ? '#e5e7eb' : '#111827' }}>
                          {n.title}
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: isDarkMode ? '#cbd5e1' : '#4b5563',
                            marginTop: '2px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={n.body}
                        >
                          {n.body}
                        </div>
                        <div style={{ fontSize: '11px', color: isDarkMode ? '#9ca3af' : '#6b7280', marginTop: '6px' }}>
                          {formatRelativeTime(n.createdAtUtc)}
                        </div>
                      </div>

                      <div style={{ color: isDarkMode ? '#9ca3af' : '#9ca3af', paddingTop: '2px' }}>{'>'}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Saved stocks + quick login */}
        <details
          ref={authMenuDetailsRef}
          onToggle={(e) => {
            const isOpen = Boolean((e.currentTarget as HTMLDetailsElement).open);
            setAuthMenuOpen(isOpen);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          <summary
            id="auth-menu-button"
            aria-label="Account menu"
            title="Account"
            style={{
              listStyle: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              padding: '9px 14px',
              gap: 10,
              border: `1px solid ${isDarkMode ? '#3B82F6' : '#2F58F2'}`,
              borderRadius: 20,
              backgroundColor: isDarkMode ? '#1E293B' : '#F1F7FF',
              boxSizing: 'border-box',
              fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
              fontWeight: 400,
              fontSize: 16,
              lineHeight: '100%',
              letterSpacing: '0%',
              color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
              userSelect: 'none',
            }}
          >
            <div style={avatarStyle}>
              {avatarLetter}
            </div>
          </summary>

          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '2.75rem',
              minWidth: '260px',
              maxWidth: '360px',
              backgroundColor: isDarkMode ? '#111827' : '#ffffff',
              border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
              borderRadius: '0.75rem',
              padding: '0.75rem',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              zIndex: 1100,
            }}
          >
            <div id="auth-menu-dropdown" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.25rem 0.25rem 0.5rem 0.25rem' }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '999px',
                      backgroundColor: isDarkMode ? '#1E293B' : '#f8fafc',
                      border: `1px solid ${isDarkMode ? '#3B82F6' : '#e5e7eb'}`,
                      display: 'grid',
                      placeItems: 'center',
                      fontWeight: 800,
                      color: isDarkMode ? '#111827' : '#111827',
                      flex: '0 0 auto',
                    }}
                  >
                    {avatarLetter}
                  </div>

                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: 800, color: isDarkMode ? '#e5e7eb' : '#111827', lineHeight: 1.2 }}>
                      {authDisplayName}
                    </div>
                    {authEmail ? (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: '0.82rem',
                          color: isDarkMode ? '#9ca3af' : '#6b7280',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={authEmail}
                      >
                        {authEmail}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div style={{ padding: '0.25rem 0.25rem 0.75rem 0.25rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: isDarkMode ? '#0b1220' : '#f3f4f6',
                      border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                      borderRadius: '0.75rem',
                      padding: '0.25rem',
                      gap: '0.25rem',
                    }}
                  >
                    <button
                      id="theme-light"
                      type="button"
                      aria-label="Light mode"
                      aria-pressed={!isDarkMode}
                      onClick={() => {
                        if (isDarkMode) onToggleTheme();
                      }}
                      style={{
                        flex: 1,
                        height: 40,
                        border: 'none',
                        borderRadius: '0.65rem',
                        backgroundColor: !isDarkMode ? '#111827' : 'transparent',
                        color: !isDarkMode ? '#ffffff' : (isDarkMode ? '#e5e7eb' : '#111827'),
                        display: 'grid',
                        placeItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <img
                        src={isDarkMode ? '/images/darkmode/lightmode.svg' : '/images/lightmode.svg'}
                        alt=""
                        aria-hidden="true"
                        style={{ width: 18, height: 18, display: 'block', color: 'inherit', filter: (isDarkMode ? 'none' : 'invert(1)') }}
                      />
                    </button>

                    <button
                      id="theme-dark"
                      type="button"
                      aria-label="Dark mode"
                      aria-pressed={isDarkMode}
                      onClick={() => {
                        if (!isDarkMode) onToggleTheme();
                      }}
                      style={{
                        flex: 1,
                        height: 40,
                        border: 'none',
                        borderRadius: '0.65rem',
                        backgroundColor: isDarkMode ? '#111827' : 'transparent',
                        color: isDarkMode ? '#ffffff' : '#111827',
                        display: 'grid',
                        placeItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <img
                        src={isDarkMode ? '/images/darkmode/darkmode.svg' : '/images/darkmode.svg'}
                        alt=""
                        aria-hidden="true"
                        style={{ width: 18, height: 18, display: 'block', filter: 'none' }}
                      />
                    </button>
                  </div>
                </div>

                <div style={{ height: 1, backgroundColor: isDarkMode ? '#374151' : '#e5e7eb', margin: '0.25rem 0' }} />

                {isConnected ? (
                  <>
                    <button
                      id="auth-menu-profile"
                      type="button"
                      onClick={() => {
                        closeAuthMenu();
                        // Best-effort: admin/profile area (URL-based entry point)
                        try {
                          window.location.assign('/administrator');
                        } catch {
                          // ignore
                        }
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        background: 'transparent',
                        border: 'none',
                        padding: '0.75rem 0.5rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: isDarkMode ? '#e5e7eb' : '#111827',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      View My Profile
                    </button>

                    <div style={{ height: 1, backgroundColor: isDarkMode ? '#374151' : '#e5e7eb' }} />

                    <button
                      id="auth-menu-report-link"
                      type="button"
                      onClick={() => {
                        closeAuthMenu();
                        setBugReportError(null);
                        setBugReportSuccess(null);
                        setIsBugReportModalOpen(true);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        background: 'transparent',
                        border: 'none',
                        padding: '0.75rem 0.5rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: isDarkMode ? '#e5e7eb' : '#111827',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      Report bug / feedback
                    </button>

                    <div style={{ height: 1, backgroundColor: isDarkMode ? '#374151' : '#e5e7eb' }} />

                    <button
                      id="auth-menu-logout"
                      type="button"
                      onClick={() => {
                        closeAuthMenu();
                        onLogout?.();
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        background: 'transparent',
                        border: 'none',
                        padding: '0.75rem 0.5rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: isDarkMode ? '#F9FAFB' : '#b91c1c',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M10 17l-1 0a6 6 0 0 1 0-12h1"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M14 7l5 5-5 5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M19 12H10"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      Log out
                    </button>
                  </>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.25rem 0.25rem 0 0.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 800, color: isDarkMode ? '#e5e7eb' : '#111827' }}>
                        User
                      </div>
                      <select
                        id="auth-menu-connect-user"
                        value={quickLoginUser}
                        onChange={(e) => setQuickLoginUser(e.target.value)}
                        disabled={authBusy}
                        style={{
                          flex: 1,
                          padding: '0.5rem 0.6rem',
                          borderRadius: '0.65rem',
                          border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                          backgroundColor: isDarkMode ? '#0b1220' : '#ffffff',
                          color: isDarkMode ? '#e5e7eb' : '#111827',
                          fontWeight: 800,
                          fontSize: '0.85rem',
                        }}
                      >
                        {quickLoginUsers.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </div>

                    {savedStocksError ? (
                      <div style={{ fontSize: '0.8125rem', color: '#ef4444' }}>{savedStocksError}</div>
                    ) : null}

                    <button
                      id="auth-menu-connect"
                      type="button"
                      onClick={() => {
                        closeAuthMenu();
                        const password = quickLoginPasswordByUser.get(quickLoginUser) ?? quickLoginUser;
                        onQuickLogin?.(quickLoginUser, password);
                      }}
                      disabled={authBusy || !onQuickLogin}
                      style={{
                        padding: '0.6rem 0.75rem',
                        borderRadius: '0.65rem',
                        border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                        backgroundColor: authBusy ? (isDarkMode ? '#374151' : '#e5e7eb') : '#2F58F2',
                        color: '#ffffff',
                        fontWeight: 800,
                        cursor: authBusy ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {authBusy ? 'Connecting…' : `Connect (${quickLoginUser}/${quickLoginPasswordByUser.get(quickLoginUser) ?? quickLoginUser})`}
                    </button>
                  </div>
                )}
              </div>
          </div>
        </details>
      </div>
      </header>

      <BugReportModal
        open={isBugReportModalOpen}
        isDarkMode={isDarkMode}
        busy={bugReportBusy}
        error={bugReportError}
        success={bugReportSuccess}
        onClose={() => {
          if (bugReportBusy) return;
          setIsBugReportModalOpen(false);
          setBugReportError(null);
        }}
        onSubmit={submitBugReport}
      />

      {/* Stock Info Bar and Tabs Bar - Visible only on Research page */}
      {currentPage === 'Research' && (
      <div ref={researchBarsRef} style={barsContainerStyle}>
          <div style={{...barsWrapperStyle, width: '93%', marginRight: '4rem', marginLeft: '2.4rem'}}>
            {/* Stock Info Bar */}
            <div id="stock-info-bar" style={stockInfoBarStyle}>
            {/* Stock Logo */}
            <div id="stock-logo" style={stockLogoStyle}>
              <img 
                src={`https://financialmodelingprep.com/image-stock/${selectedStock}.png`}
                alt={selectedStock}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                onError={(e) => {
                  // Fallback to letter if image fails
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement!.innerHTML = `<span style="font-size: 1.5rem">${selectedStock.charAt(0)}</span>`;
                }}
              />
            </div>

            {/* Stock Name and Symbol */}
            <div id="stock-name-symbol" style={stockInfoStyle}>
              <div
                id="stock-name"
                style={{
                  ...stockNameStyle,
                  color: isResearchQuotesRefreshing ? '#8a8a8a' : stockNameStyle.color,
                }}
              >
                {displayStockName}
              </div>
              <div
                id="stock-symbol"
                style={{
                  ...stockSymbolStyle,
                  color: isResearchQuotesRefreshing ? '#8a8a8a' : stockSymbolStyle.color,
                }}
              >
                {selectedStock} | {displayExchange}
              </div>
            </div>

            {/* Stock Price */}
            <div
              id="stock-current-price"
              style={{
                ...stockPriceStyle,
                color: isResearchQuotesRefreshing ? '#8a8a8a' : stockPriceStyle.color,
              }}
            >
              {formattedPrice}
            </div>

            {/* Mini Stock Chart */}
            <svg 
              id="Intra day" 
              key={`chart-${selectedStock}-${intradaySeries.length}-${historicalPrices.length}-${chartSeries.length}`}
              width="144" 
              height="36" 
              style={{ marginLeft: '1rem', opacity: isResearchQuotesRefreshing ? 0.6 : 1 }}
            >
              {/* Positive area (above baseline) */}
              {posSegments.map((seg, i) => {
                const d = buildAreaPath(seg);
                if (!d) return null;
                return (
                  <path
                    key={`pos-fill-${selectedStock}-${i}`}
                    d={d}
                    fill="#10b981"
                    fillOpacity={0.18}
                    stroke="none"
                  />
                );
              })}

              {/* Negative area (below baseline) */}
              {negSegments.map((seg, i) => {
                const d = buildAreaPath(seg);
                if (!d) return null;
                return (
                  <path
                    key={`neg-fill-${selectedStock}-${i}`}
                    d={d}
                    fill="#ef4444"
                    fillOpacity={0.18}
                    stroke="none"
                  />
                );
              })}

              {/* Positive line segments */}
              {posSegments.map((seg, i) => (
                <polyline
                  key={`pos-line-${selectedStock}-${i}`}
                  points={seg.map((p) => `${p.x},${p.y}`).join(' ')}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}

              {/* Negative line segments */}
              {negSegments.map((seg, i) => (
                <polyline
                  key={`neg-line-${selectedStock}-${i}`}
                  points={seg.map((p) => `${p.x},${p.y}`).join(' ')}
                  fill="none"
                  stroke="#FF0052"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}

              {/* Dashed "remaining session" baseline */}
              {(() => {
                const y = midY;
                const showRemainingOnly = isResearchPage && intradaySeries.length > 0 && intradayResolved.isMarketDay && getMarketProgressNow().isMarketOpenNow && sessionProgress < 0.999;
                const x1 = showRemainingOnly ? xMax : baselineStartX;
                const x2 = baselineEndX;
                if (x2 - x1 < 0.5) return null;
                return (
                  <line
                    x1={x1}
                    y1={y}
                    x2={x2}
                    y2={y}
                    stroke={isDarkMode ? '#4b5563' : '#d1d5db'}
                    strokeWidth={1}
                    strokeDasharray="3,3"
                  />
                );
              })()}
            </svg>

            {/* Stock Daily Change and After Hours Container */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
              {/* Stock Daily Change */}
              <div
                id="stock-daily-change"
                style={{
                  ...stockChangeStyle,
                  color: isResearchQuotesRefreshing ? '#8a8a8a' : stockChangeStyle.color,
                }}
              >
                <span style={{ color: isDarkMode ? '#D1D5DB' : '#5A5A5A' }}>{formattedDate}</span>{' '}
                ({formattedPercent}) {formattedChange}
                <svg width="12" height="12" viewBox="0 0 12 12" style={{ display: 'inline', marginLeft: '4px' }}>
                  <path
                    d={isPositive ? "M6 2 L10 8 L2 8 Z" : "M6 10 L10 4 L2 4 Z"}
                    fill={isResearchQuotesRefreshing ? '#8a8a8a' : (isPositive ? '#10b981' : '#FF0052')}
                  />
                </svg>
              </div>

              {/* Extended Hours Info (Pre-Market or After Hours) */}
              {marketSession !== 'Market Open' && (
                <div id="stock-after-hours" style={{...stockDetailsStyle, fontSize: '1rem',fontWeight:'500', gap: '0.25rem'}}>
                  <span style={{ color: isDarkMode ? '#D1D5DB' : '#5A5A5A' }}>{marketSession}</span>
                  <span style={{ color: isResearchQuotesRefreshing ? '#8a8a8a' : (isAfterHoursPositive ? '#10b981' : '#FF0052') }}>
                    {formattedAfterHoursPercent} {formattedAfterHoursChange}
                  </span>
                  <span style={{ color: isResearchQuotesRefreshing ? '#8a8a8a' : (isDarkMode ? '#D1D5DB' : '#5A5A5A') }}>{formattedAfterHoursPrice}</span>
                </div>
              )}
            </div>

            {/* Next Earnings Date */}
            <div id="stock-earnings-date" style={earningsDateStyle}>
              <span style={{ color: isResearchQuotesRefreshing ? '#8a8a8a' : (isDarkMode ? '#D1D5DB' : '#5A5A5A') }}>Next Earnings Report:</span>{' '}
              <span style={{ color: isResearchQuotesRefreshing ? '#8a8a8a' : (isDarkMode ? '#e5e7eb' : '#0D0D0D') }}>{earningsDate}</span>
            </div>

            {/* Action Links */}
            <div id="stock-action-links" style={actionLinksStyle}>
              <span
                id="add-to-portfolio"
                style={{ ...actionLinkStyle, cursor: 'pointer' }}
                onClick={() => onAddToMyPortfolio?.()}
              >
                Add to my Portfolio
              </span>
              <span
                id="add-to-watchlist"
                style={{ ...actionLinkStyle, cursor: 'pointer' }}
                onClick={() => onAddToMyWatchlist?.()}
              >
                Add to my Watchlist
              </span>
            </div>
          </div>
          </div>

          {/* Gap between Stock Info and Tabs */}
          <div style={{ height: `${barsVerticalGapPx}px` }} />

          {/* Tabs Bar */}
          <div style={{ ...barsWrapperStyle, width: '93%', marginRight: '4rem', marginLeft: '2.4rem' }}>
          <div id="tabs-bar" style={tabsBarStyle}>
            {tabs.map((tab) => (
              <div
                key={tab}
                style={tabStyle(tab === activeTab, false)}
                onClick={() => {
                  if (onTabChange) {
                    onTabChange(tab);
                  }
                }}
              >
                {tab}
              </div>
            ))}
          </div>

          {/* Period Selector for Balance Sheet */}
          {activeTab === 'Balance Sheet' && (
            <div style={{
            
            }}>
            </div>
          )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
